/**
 * CallNotes — reusable notes section for a call.
 * Shows existing notes + add note input.
 * Used in: call history expanded row, floating call widget.
 */

import { useState, useEffect, useRef } from 'react';
import { StickyNote, Send, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCallStore } from '@/stores/call-store';
import { useAppUsersStore } from '@/stores/app-users-store';
import { useAuthStore } from '@/stores/auth-store';
import type { CallNote } from '@/types/voice';

interface CallNotesProps {
  callSid: string;
  /** The call's other legs — after a transfer each agent's notes are stored
   *  under the leg they were on, so all of them must be read. */
  extraSids?: string[];
  /** This is the agent's own live call leg — see fetchCallNotes. */
  ownLeg?: boolean;
  customerNumber?: string;
  /** Compact mode for floating call widget */
  compact?: boolean;
  className?: string;
}

/**
 * Unsent note drafts, keyed by callSid. Module-level so a draft survives the
 * floating widget being auto-dismissed (which unmounts this component) — the
 * text is restored if the notes UI for that call is opened again.
 */
const noteDrafts = new Map<string, string>();

export function CallNotes({ callSid, extraSids, ownLeg, customerNumber, compact, className }: CallNotesProps) {
  const notes = useCallStore((s) => s.callNotes[callSid]);
  const fetchCallNotes = useCallStore((s) => s.fetchCallNotes);
  const addCallNote = useCallStore((s) => s.addCallNote);

  const [text, setTextState] = useState(() => noteDrafts.get(callSid) || '');
  const [adding, setAdding] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const setText = (value: string) => {
    setTextState(value);
    noteDrafts.set(callSid, value);
  };

  // Restore the draft when this instance is switched to a different call
  useEffect(() => {
    setTextState(noteDrafts.get(callSid) || '');
  }, [callSid]);

  // Fetch on mount and whenever the call changes — including when a thread is
  // already cached. The cache goes stale in a way that looks like data loss:
  // the live widget loads the thread while it is still empty and caches [],
  // then a note added during the call is written to the server but the history
  // panel, mounting later with the same SID, sees a cached array and never
  // re-reads — showing "No notes yet" for a note that exists.
  // The cached list stays on screen while the fresh read runs.
  const legsKey = (extraSids || []).join(',');
  useEffect(() => {
    fetchCallNotes(callSid, extraSids, ownLeg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callSid, legsKey]);

  const handleAdd = async () => {
    if (!text.trim() || adding) return;
    setAdding(true);
    await addCallNote(callSid, text, customerNumber);
    noteDrafts.delete(callSid);
    setTextState('');
    setAdding(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAdd();
    }
  };

  const loading = notes === undefined;

  return (
    <div className={cn('space-y-2', className)}>
      {/* Add note input */}
      {compact ? (
        <div className="flex items-end gap-1.5 bg-slate-700/50 rounded-lg p-1.5">
          <textarea
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add a note..."
            rows={1}
            className="flex-1 resize-none bg-transparent text-xs text-white placeholder:text-slate-400 min-h-[24px] focus:outline-none"
          />
          <button
            onClick={handleAdd}
            disabled={!text.trim() || adding}
            className="shrink-0 rounded-md p-1.5 text-slate-300 hover:text-white hover:bg-slate-600 transition-colors disabled:opacity-30"
            title="Add note (Enter)"
          >
            {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          </button>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-background shadow-sm overflow-hidden">
          <textarea
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Write a note..."
            rows={2}
            className="w-full resize-none bg-transparent px-3.5 pt-3 pb-1 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none"
          />
          <div className="flex items-center justify-between px-3 pb-2">
            <span className="text-[10px] text-muted-foreground/50">Press Enter to add</span>
            <button
              onClick={handleAdd}
              disabled={!text.trim() || adding}
              className="flex items-center gap-1.5 rounded-lg bg-primary/10 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/20 transition-colors disabled:opacity-30"
              title="Add note"
            >
              {adding ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              Add
            </button>
          </div>
        </div>
      )}

      {/* Existing notes */}
      {loading ? (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground py-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading notes...
        </div>
      ) : notes && notes.length > 0 ? (
        <div className={cn('space-y-1.5', compact && 'max-h-24 overflow-y-auto')}>
          {notes.map((note) => (
            <NoteItem key={note.id} note={note} compact={compact} />
          ))}
        </div>
      ) : (
        <p className={cn('text-xs py-3 text-center', compact ? 'text-slate-500' : 'text-muted-foreground/50')}>
          No notes yet
        </p>
      )}
    </div>
  );
}

function NoteItem({ note, compact }: { note: CallNote; compact?: boolean }) {
  const timeStr = formatNoteTime(note.createdAt);
  const orgUsers = useAppUsersStore((s) => s.orgUsers);
  const currentUser = useAuthStore((s) => s.user);

  const orgUser = Object.values(orgUsers).find((u) => u.email === note.authorEmail);
  const isCurrentUser = !orgUser && currentUser?.email === note.authorEmail;
  const displayName = note.authorName || orgUser?.name || (isCurrentUser ? currentUser?.displayName : null) || note.authorEmail.split('@')[0];
  const picture = orgUser?.picture || (isCurrentUser ? currentUser?.photoURL : null);
  const initial = (displayName[0] || '?').toUpperCase();

  // Color from email hash
  const COLORS = ['bg-blue-500/15 text-blue-600', 'bg-emerald-500/15 text-emerald-600', 'bg-violet-500/15 text-violet-600', 'bg-amber-500/15 text-amber-600', 'bg-rose-500/15 text-rose-600', 'bg-cyan-500/15 text-cyan-600'];
  const colorIdx = note.authorEmail.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length;

  if (compact) {
    return (
      <div className="rounded-md bg-slate-700/30 px-2.5 py-1.5">
        <div className="flex items-center gap-1.5 mb-0.5">
          <StickyNote className="h-3 w-3 shrink-0 text-amber-400" />
          <span className="text-[10px] font-medium text-amber-400">{displayName}</span>
          <span className="text-[10px] text-slate-500">{timeStr}</span>
        </div>
        <p className="text-xs whitespace-pre-wrap text-slate-200">{note.text}</p>
      </div>
    );
  }

  return (
    <div className="flex gap-3 rounded-lg bg-amber-50 dark:bg-amber-500/5 border border-amber-200/50 dark:border-amber-500/10 p-3">
      {/* Author avatar */}
      {picture ? (
        <img src={picture} alt="" className="h-7 w-7 rounded-full object-cover shrink-0 mt-0.5" />
      ) : (
        <div className={cn('h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 mt-0.5', COLORS[colorIdx])}>
          {initial}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-foreground">{displayName}</span>
          <span className="text-[11px] text-muted-foreground">{timeStr}</span>
        </div>
        <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{note.text}</p>
      </div>
    </div>
  );
}

function formatNoteTime(ts: number): string {
  const date = new Date(ts);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
